import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ALIANZA_CARRUSEL_MS } from '../../data/perfil-alianza.content';
import { AlianzaCarruselFade } from './perfil-alianza-carrusel-fade';
import { sanitizeAlianzaHtml } from './perfil-alianza-html.util';
import { AlianzaEstrategica } from './perfil-alianza.model';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

@Component({
  selector: 'app-perfil-alianza-hub-card',
  templateUrl: './perfil-alianza-hub-card.component.html',
  styleUrls: ['./perfil-alianza-hub-card.component.scss'],
  standalone: false,
})
export class PerfilAlianzaHubCardComponent implements OnChanges, OnDestroy {
  @Input() alianzas: AlianzaEstrategica[] = [];

  indice = 0;
  readonly fade = new AlianzaCarruselFade();

  private timer: ReturnType<typeof setInterval> | null = null;
  private hoverPaused = false;
  private focusPaused = false;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    private readonly analyticsTag: AnalyticsTagService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['alianzas']) {
      return;
    }
    this.indice = 0;
    this.fade.destroy();
    this.restartTimer();
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.fade.destroy();
  }

  get actual(): AlianzaEstrategica | null {
    return this.alianzas[this.indice] ?? null;
  }

  get mostrarCarrusel(): boolean {
    return this.alianzas.length > 1;
  }

  html(value: string | null | undefined): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(sanitizeAlianzaHtml(value));
  }

  onCta(): void {
    const url = this.actual?.urlForward?.trim();
    if (!url) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.actual?.nombreBoton ?? '',
      ctaSection: 'hub',
      ctaComponent: 'perfil-alianza-hub-card',
      ctaElementId: 'btn-alianza-hub-cta',
      hu: '8338',
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  irA(indice: number): void {
    if (indice < 0 || indice >= this.alianzas.length) {
      return;
    }
    this.cambiarIndice(indice);
    this.restartTimer();
  }

  onCarruselPause(): void {
    this.hoverPaused = true;
  }

  onCarruselResume(): void {
    this.hoverPaused = false;
  }

  onCarruselFocusIn(): void {
    this.focusPaused = true;
  }

  onCarruselFocusOut(): void {
    this.focusPaused = false;
  }

  private get paused(): boolean {
    return this.hoverPaused || this.focusPaused;
  }

  private cambiarIndice(next: number): void {
    this.fade.goTo(next, this.indice, (i) => {
      this.indice = i;
    }, this.cdr);
  }

  private restartTimer(): void {
    this.stopTimer();
    if (this.alianzas.length < 2 || this.prefersReducedMotion()) {
      return;
    }
    this.timer = setInterval(() => {
      if (this.paused || this.alianzas.length < 2) {
        return;
      }
      this.cambiarIndice((this.indice + 1) % this.alianzas.length);
    }, ALIANZA_CARRUSEL_MS);
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
